'use strict';

import { cn_plugin_option } from '../utils/cn_plugin_option';
import { cn_facing } from './cn_facing';
import { CN_FACING_SUPPORTS } from './cn_model_constants';

//***********************************************************************************
//***********************************************************************************
//******     CN-Map    **************************************************************
//******     Copyright(C) 2019-2020 EnerBIM                        ******************
//***********************************************************************************
//***********************************************************************************

/** @type {cn_facing_family[]} */
const FACING_FAMILIES = [];

//***********************************************************************************
/**
 * @class cn_facing
 * Class to modelize the facing of a wall, slab, etc.
 */
export class cn_facing_family {
    //***********************************************************************************
    /**
     * Constructor
     * @param {string} label
     * @param {string} code
     * @param {string[]} supports
     */
    constructor(label, code, supports) {
        this.label = label;
        this.code = code;
        this.supports = supports;
        this.facings = [];
    }

    /**
     * Fills facings with the appropriate facings from the building
     * @param {import('./cn_building').cn_building} building
     */
    populate(building) {
        this.facings = building.get_facing_types().filter(ft => this.matches(ft));
    }

    /**
     * Returns true if the facing family is compatible with the type
     * @param {cn_facing} facing
     * @returns
     */
    matches(facing) {
        return true;
    }

    /**
     * Checks a list of supports, during unserailization.
     * @param {string[]} supports
     * @returns  {string[]}
     */
    static check_supports(supports) {
        if (supports.length == 1 && supports[0] == "other")
            return [
                CN_FACING_SUPPORTS.OPENING,
                CN_FACING_SUPPORTS.NICHE,
                CN_FACING_SUPPORTS.STAIR,
                CN_FACING_SUPPORTS.OBJECT,
                CN_FACING_SUPPORTS.BEAM,
                CN_FACING_SUPPORTS.COLUMN,
                CN_FACING_SUPPORTS.PIPE,
                CN_FACING_SUPPORTS.MOLDING];

        let families = cn_facing_family.families();
        let check_supports = supports.filter(s => families.some(f => f.supports.includes(s)));
        if (check_supports.length == 0) {
            let default_family = families.find(f => f.code == "other");
            if (default_family) check_supports = default_family.supports.concat([]);
        }
        return check_supports;
    }

    /**
     * Returns the current populated list of facing groups, with facings set from a given building.
     * @param {import('./cn_building').cn_building} building
     * @returns {cn_facing_family[]}
     */
    static get_facing_groups(building) {
        return cn_facing_family.families().map(f => {
            let facing_group = new cn_facing_family(f.label, f.code, f.supports);
            facing_group.populate(building);
            return facing_group;
        });
    }

    /**
     * Returns the current populated list of facing groups, with facings set from a given building.
     * @param {*} building
     * @returns
     */
    static get_facing_group(support, building) {
        let f = cn_facing_family.family_from_support(support);
        let facing_group = new cn_facing_family(f.label, f.code, f.supports);
        //facing_group.facings = building.get_facing_types().filter(ft => ft.supports.includes(support));
        facing_group.populate(building);
        return facing_group;
    }

    /**
     * Returns the current list of facing families. Thise does not contain any facings.
     * @returns
     */
    static families() {
        if (FACING_FAMILIES.length == 0) {
            let families = [];
            families.push(new cn_facing_family("Sols", "floor", [CN_FACING_SUPPORTS.FLOOR]));
            families.push(new cn_facing_family("Plafonds", "ceiling", [CN_FACING_SUPPORTS.CEILING]));
            families.push(new cn_facing_family("Murs", "wall", [CN_FACING_SUPPORTS.WALL]));
            families.push(new cn_facing_family("Façades", "facade", [CN_FACING_SUPPORTS.FACADE]));
            families.push(new cn_facing_family("Extérieur", "exterior", [CN_FACING_SUPPORTS.EXTERIOR]));
            families.push(new cn_facing_family("Objets", "objects", [CN_FACING_SUPPORTS.OBJECT]));
            cn_facing_family.set_families(families)
        }
        return FACING_FAMILIES;
    }

    /**
     * Sets families. Will automatically add a last 'others' family with all non-addressed supports.
     * @param {cn_facing_family[]} families
     */
    static set_families(families) {
        FACING_FAMILIES.splice(0, FACING_FAMILIES.length);

        let facing_supports = [];
        for (var k in CN_FACING_SUPPORTS) facing_supports.push(CN_FACING_SUPPORTS[k]);
        families.forEach(f => {
            let supports = f.supports.filter(s => {
                let i = facing_supports.indexOf(s);
                if (i < 0) return false;
                facing_supports.splice(i, 1);
                return true;
            });
            if (supports.length)
                FACING_FAMILIES.push(new cn_facing_family(f.label, f.code, supports));
        });
        if (facing_supports.length)
            FACING_FAMILIES.push(new cn_facing_family("Autres", "other", facing_supports));
    }

    /**
     * Returns the facing family for a given support.
     * @param {string} support
     * @returns {cn_facing_family}
     */
    static family_from_support(support) {
        return cn_facing_family.families().find(f => f.supports.includes(support)) || null;
    }

    /**
     * returns the families that intersects with given support list
     * @param {string[]} supports
     * @returns {cn_facing_family[]}
     */
    static get_filtered_families(supports) {
        return cn_facing_family.families().filter(f => f.supports.some(s => supports.includes(s)));
    }

    /**
     * returns the families used for exterior
     * @returns {cn_facing_family[]}
     */
    static get_exterior_families() {
        return cn_facing_family.get_filtered_families([
            CN_FACING_SUPPORTS.EXTERIOR,
            CN_FACING_SUPPORTS.OBJECT]);
    }

    /**
     * returns the families used for a storey
     * @returns {cn_facing_family[]}
     */
    static get_storey_families() {
        let supports = [
            CN_FACING_SUPPORTS.FLOOR,
            CN_FACING_SUPPORTS.CEILING,
            CN_FACING_SUPPORTS.WALL,
            CN_FACING_SUPPORTS.FACADE,
            CN_FACING_SUPPORTS.OPENING,
            CN_FACING_SUPPORTS.NICHE,
            CN_FACING_SUPPORTS.STAIR,
            CN_FACING_SUPPORTS.OBJECT,
            CN_FACING_SUPPORTS.BEAM,
            CN_FACING_SUPPORTS.COLUMN,
            CN_FACING_SUPPORTS.PIPE];
        if (cn_plugin_option.moldings) supports.push(CN_FACING_SUPPORTS.MOLDING);
        return cn_facing_family.get_filtered_families(supports);
    }

    /**
     * returns the families used for a roof
     * @returns {cn_facing_family[]}
     */
    static get_roof_families() {
        return cn_facing_family.get_filtered_families([
            CN_FACING_SUPPORTS.WALL,
            CN_FACING_SUPPORTS.FACADE,
            CN_FACING_SUPPORTS.OPENING,
            CN_FACING_SUPPORTS.OBJECT]);
    }

    /**
     * returns the families used for 3D
     * @returns {cn_facing_family[]}
     */
    static get_3d_families() {
        return cn_facing_family.get_filtered_families([
            CN_FACING_SUPPORTS.FLOOR,
            CN_FACING_SUPPORTS.CEILING,
            CN_FACING_SUPPORTS.WALL,
            CN_FACING_SUPPORTS.FACADE,
            CN_FACING_SUPPORTS.EXTERIOR,
            CN_FACING_SUPPORTS.OPENING,
            CN_FACING_SUPPORTS.STAIR,
            CN_FACING_SUPPORTS.OBJECT,
            CN_FACING_SUPPORTS.BEAM,
            CN_FACING_SUPPORTS.COLUMN,
            CN_FACING_SUPPORTS.PIPE]);
    }
}
